<template>
  <div v-show="isSidebarOpen"
    class="sidebar-button fixed inset-y-0 right-0 z-50 w-3/4 max-w-md bg-white shadow-xl pt-6 transform transition-transform duration-300 ease-in-out translate-x-0 flex flex-col overflow-y-auto"
    :class="{ 'translate-x-0': isSidebarOpen, 'translate-x-full': !isSidebarOpen }"
    :style="{ maxHeight: `${windowHeight}px` }">


    <div class="flex-grow px-6">
      <!-- Close Button for Sidebar -->
      <button @click="closeSidebar"
        class="absolute top-4 right-4 text-red-500 transition-transform duration-300 ease-in-out hover:rotate-90 hover:scale-110">
        <font-awesome-icon icon="times" class="h-6 w-6" />
      </button>

      <!-- Logo or Event Type Name at the Top of Sidebar -->
      <div class="flex items-center justify-center w-full mb-6">
        <a :href="$TWORK_INFO.url?.homepage || '#'" class="flex items-center">
          <img v-if="eventType.images?.logo" :src="eventType.images?.logo.large" alt="Logo"
            class="h-12 w-auto ev-logo" />
          <span v-else :class="['text-2xl font-bold', getColor(500, 'text')]">{{ eventType.name }}</span>
        </a>
      </div>

      <div>
        <AuthModule />
      </div>

      <!-- User Menu Section -->
      <div v-if="isLoggedIn" class="user-menu mt-10">
        <h2 class="text-lg font-bold text-customBlue mb-2 flex items-center justify-between cursor-pointer"
          @click="toggleUserMenu">
          {{ $translate('Account Settings') }}
          <font-awesome-icon :icon="isUserMenuCollapsed ? 'chevron-down' : 'chevron-up'" class="ml-2" />
        </h2>

        <!-- Menu Items -->
        <ul v-show="!isUserMenuCollapsed" class="text-gray-700 transition-all duration-300 ease-in-out">
          <li @click="goToAccountSettings" :class="[getColor(500, 'hover:text')]" class="px-4 pb-2 cursor-pointer">
            {{ $translate('Settings') }}
          </li>
          <li @click="goToPayments" :class="[getColor(500, 'hover:text')]" class="px-4 py-2 cursor-pointer">
            {{ $translate('Payments & Invoices') }}
          </li>
          <li @click="goToBillingAdress" :class="[getColor(500, 'hover:text')]" class="px-4 py-2 cursor-pointer">
            {{ $translate('Billing Address') }}
          </li>
          <li @click="goToPreferences" :class="[getColor(500, 'hover:text')]" class="px-4 py-2 cursor-pointer">
            {{ $translate('Preferences') }}
          </li>
          <!-- <li @click="goToCertificates" :class="[getColor(500, 'hover:text')]"
            class="px-4 py-2 cursor-pointer">
            {{ $translate('Certificates') }}
          </li>
          <li @click="goToCoupons" :class="[getColor(500, 'hover:text')]"
            class="px-4 py-2 cursor-pointer">
            {{ $translate('Coupons') }}
          </li>
          <li @click="goToTickets" :class="[getColor(500, 'hover:text')]"
            class="px-4 py-2 cursor-pointer">
            {{ $translate('Tickets') }}
          </li>
          <li @click="goToVouchers" :class="[getColor(500, 'hover:text')]"
            class="px-4 py-2 cursor-pointer">
            {{ $translate('Vouchers & Gift Cards') }}
          </li>
          <li @click="goToPartners" :class="[getColor(500, 'hover:text')]"
            class="px-4 py-2 cursor-pointer">
            {{ $translate('Partner Program') }}
          </li>
          <li @click="goToContracts" :class="[getColor(500, 'hover:text')]"
            class="px-4 py-2 cursor-pointer">
            {{ $translate('Contracts') }}
          </li> -->
          <li @click="handleLogout" class="mobile-sidebar-logout px-4 py-2 text-red-600 cursor-pointer">
            {{ $translate('Logout') }}
          </li>
        </ul>
      </div>


      <!-- Event Types Section -->
      <div v-if="eventTypes.length > 1" class="mt-10">
        <h2 class="text-lg font-bold text-customBlue mb-2 flex items-center justify-between cursor-pointer"
          @click="toggleEventTypes">
          {{ $translate('Event Types') }}
          <font-awesome-icon :icon="isEventTypesCollapsed ? 'chevron-down' : 'chevron-up'" class="ml-2" />
        </h2>
        <ul v-show="!isEventTypesCollapsed"
          class="flex flex-col space-y-2 text-lg text-gray-700 transition-all duration-300 ease-in-out">
          <li v-for="eventTypeItem in eventTypes" :key="eventTypeItem.type">
            <router-link :to="'/' + eventTypeItem.path" exact-active-class="text-lighterCustomBlue"
              :class="[getColor(700, 'text'), getColor(400, 'hover:text'), 'transition-colors']">
              {{ eventTypeItem.plural }}
            </router-link>
          </li>
        </ul>
      </div>

      <!-- Search Section -->
      <div v-if="showSearchInput" class="mt-10">
        <h2 class="text-lg font-bold overflow text-customBlue mb-2">{{ $translate('Search') }}</h2>
        <div class="relative w-full">
          <span class="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
            <font-awesome-icon icon="search" />
          </span>
          <input type="text" v-model="localSearchQuery" @input="debouncedHandleSearch"
            :placeholder="translatedPlaceholder"
            :class="['border border-gray-300 rounded pl-10 pr-4 py-2 w-full focus:outline-none focus:ring-2', getColor(500, 'focus:ring')]" />
        </div>
      </div>
    </div>

    <!-- Footer Links Section -->
    <!-- Footer Links Section - Show only if legal pages are enabled -->
    <div v-if="enable_legalpages" class="flex flex-col pt-4 px-6 border-t border-gray-200 mt-10">
      <ul class="flex flex-col justify-evenly items-start mb-4 gap-4 text-sm">
        <li class="mb-2">
          <router-link :to="{ name: 'LegalPages', params: { id: $systemPath('terms') } }"
            :class="[getColor(500, 'hover:text')]">
            {{ $translate('Terms & Conditions') }}
          </router-link>
        </li>
        <li class="mb-2">
          <router-link :to="{ name: 'LegalPages', params: { id: $systemPath('privacy') } }"
            :class="[getColor(500, 'hover:text')]">
            {{ $translate('Privacy Policy') }}
          </router-link>
        </li>
        <li class="mb-2">
          <router-link :to="{ name: 'LegalPages', params: { id: $systemPath('cookiePolicy') } }"
            :class="[getColor(500, 'hover:text')]">
            {{ $translate('Cookies Policy') }}
          </router-link>
        </li>
        <li>
          <router-link :to="{ name: 'LegalPages', params: { id: $systemPath('contact') } }"
            :class="[getColor(500, 'hover:text')]">
            {{ $translate('contact_us') }}
          </router-link>
        </li>
      </ul>
    </div>

    <footer class="bg-blue-100 text-white py-6 mt-auto">
      <div class="container mx-auto px-7 flex flex-col justify-between items-center">
        <div class="flex gap-4 mb-4">
          <a v-if="$TWORK_INFO.url.social.facebook" :href="$TWORK_INFO.url.social.facebook" target="_blank">
            <font-awesome-icon :icon="['fab', 'facebook']" class="hover:text-white h-6 w-6 text-blue-500" />
          </a>
          <a v-if="$TWORK_INFO.url.social.twitter" :href="$TWORK_INFO.url.social.twitter" target="_blank">
            <font-awesome-icon :icon="['fab', 'twitter']" class="hover:text-white h-6 w-6 text-blue-400" />
          </a>
          <a v-if="$TWORK_INFO.url.social.linkedin" :href="$TWORK_INFO.url.social.linkedin" target="_blank">
            <font-awesome-icon :icon="['fab', 'linkedin']" class="hover:text-white h-6 w-6 text-blue-700" />
          </a>
          <a v-if="$TWORK_INFO.url.social.youtube" :href="$TWORK_INFO.url.social.youtube" target="_blank">
            <font-awesome-icon :icon="['fab', 'youtube']" class="hover:text-white h-6 w-6 text-red-500" />
          </a>
        </div>

        <div class="text-sm text-gray-700">
          &copy; {{ new Date().getFullYear() }} {{ $TWORK_INFO.name }}.
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, toRef, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { getUserData, logout } from '@/utils/cookie';
import { getColorClass, getEventTypes, getSystemPath } from '@/utils/helper';
import AuthModule from '@/components/default/module/sidebar/AuthModule.vue';
import { translate } from '@/utils/translate';
import debounce from 'lodash/debounce';

// Accept props
const props = defineProps({
  eventType: Object,
  isSidebarOpen: Boolean,
  showSearchInput: Boolean,
  searchQuery: String,
  getColor: {
    type: Function,
    required: false,
    default: (shade = 500, prefix = 'text') => `${prefix}-${shade}`
  },
});

const isSidebarOpen = toRef(props, 'isSidebarOpen');
const emit = defineEmits(['close-sidebar', 'update-search-query']);
const router = useRouter();
const localSearchQuery = ref(props.searchQuery);

const isUserMenuCollapsed = ref(true);
const isEventTypesCollapsed = ref(true);
const windowHeight = ref(window.innerHeight);

const updateWindowHeight = () => {
  windowHeight.value = window.innerHeight;
};

// Reactive variables
const isLoggedIn = ref(false);
const user = ref({});

// Enable legal pages condition
const enable_legalpages = ref(window.vueAppConfig?.settings?.enable_legalpages || false);

// Close sidebar
const closeSidebar = () => {
  emit('close-sidebar');
};

const checkScreenHeight = () => {
  const isShortScreen = window.innerHeight < 1100;
  if (isShortScreen) {
    isUserMenuCollapsed.value = true;  // Colapsar por defecto si es menor a 1100px
    isEventTypesCollapsed.value = true;  // Colapsar los tipos de eventos también
  }
};



// Watch for changes in the search query prop and update local value
watch(
  () => props.searchQuery,
  (newVal) => {
    localSearchQuery.value = newVal;
  }
);

// Handle search input and emit updated value
const handleSearchInput = () => {
  emit('update-search-query', localSearchQuery.value);
};

const toggleUserMenu = () => {
  isUserMenuCollapsed.value = !isUserMenuCollapsed.value;
};

const toggleEventTypes = () => {
  isEventTypesCollapsed.value = !isEventTypesCollapsed.value;
};

// Fetch user data on component mount
onMounted(() => {
  const userData = getUserData();
  if (userData && userData.type === 'partner') {
    isLoggedIn.value = true;
    user.value = userData;
  }

  checkScreenHeight();
  window.addEventListener('resize', checkScreenHeight);

  window.addEventListener('resize', updateWindowHeight);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkScreenHeight);
  window.removeEventListener('resize', updateWindowHeight);
});

// Computed properties
const eventTypes = computed(() => getEventTypes());
const getColor = (shade = 500, prefix = 'text') => {
  return getColorClass(props.eventType.type, shade, prefix);
};

const translatedPlaceholder = computed(() => translate('Search events...'));

// Methods
const goToAccountSettings = () => {
  router.push({ path: `/${getSystemPath('account')}/${getSystemPath('settings')}` });
};

const goToPayments = () => {
  router.push({ path: `/${getSystemPath('account')}/${getSystemPath('payments')}` });
};

const goToBillingAdress = () => {
  router.push({ path: `/${getSystemPath('account')}/${getSystemPath('billingAddress')}` });
};

const goToPreferences = () => {
  router.push({ path: `/${getSystemPath('account')}/${getSystemPath('preferences')}` });
};

// const goToCertificates = () => {
//   router.push({ path: `/${getSystemPath('account')}/${getSystemPath('preferences')}` });
// };

// const goToCoupons = () => {
//   router.push({ path: `/${getSystemPath('account')}/${getSystemPath('preferences')}` });
// };

// const goToTickets = () => {
//   router.push({ path: `/${getSystemPath('account')}/${getSystemPath('preferences')}` });
// };

// const goToVouchers = () => {
//   router.push({ path: `/${getSystemPath('account')}/${getSystemPath('preferences')}` });
// };

// const goToPartners = () => {
//   router.push({ path: `/${getSystemPath('account')}/${getSystemPath('preferences')}` });
// };

// const goToContracts = () => {
//   router.push({ path: `/${getSystemPath('account')}/${getSystemPath('preferences')}` });
// };

const handleLogout = () => {
  logout();
  router.push({ path: `/${getSystemPath('login')}` });
};

const debouncedHandleSearch = debounce(() => {
  handleSearchInput();
}, 300);
</script>

<style>
.mobile-sidebar-logout:hover {
  color: rgb(252 165 165);
}

ul {
  transition: max-height 0.3s ease-in-out;
}

.sidebar-button {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

@media (max-height: 1100px) {
  .sidebar-button {
    height: 100vh;
  }
}
</style>